import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import GenericFormError from 'app/frontend/base/components/GenericFormError'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { createTeam } from 'app/frontend/base/api/team'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

const formSchema = z.object({
  name: z.string().min(2).max(20),
})

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
}

const NewTeamModal = ({ open, setOpen }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>()
  const { t } = useTranslation()
  const { guild, guildReference, currentTeam } = useGuild()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema> & { genericError?: string }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: guild?.teamReferences?.length ? '' : 'Main',
    },
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (!guildReference) return

    setError(undefined)
    setSubmitting(true)
    createTeam(guildReference.id, values)
      .then(async (response) => {
        toast.success(t('dashboard.team.created-message'))
        setOpen(false)
        setSubmitting(false)
        form.reset()

        await queryClient.invalidateQueries({
          queryKey: ['guild', { path: guildReference.path }],
        })

        navigate(response.team.path, {
          state: {
            guildReference: response.guild,
            teamReference: response.team,
          },
        })
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setSubmitting(false)
        setError(response.response.data.message)
      })
  }

  const teamLimitReached =
    !guild || guild.teamLimit <= guild.teamReferences?.length

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dashboard.team.create-new')}</DialogTitle>
        </DialogHeader>

        {teamLimitReached && (
          <Alert variant='warning' className='mb-4'>
            <AlertTitle>{t('dashboard.team.team-limit-reached')}</AlertTitle>
            <AlertDescription className='flex items-center justify-between gap-2'>
              {t('dashboard.team.team-limit-reached-description')}
              <Link
                to={`${
                  currentTeam?.path ?? guild?.teamReferences?.[0]?.path
                }/premium`}
                onClick={() => setOpen(false)}
              >
                <Button
                  variant='outline'
                  type='button'
                  size='sm'
                  className='text-foreground'
                >
                  {t('shared.premium')}
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} id='new-team-form'>
            <div className='flex gap-2'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='grow'>
                    <FormLabel>Team name</FormLabel>
                    <FormControl>
                      <Input placeholder='Team name' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <GenericFormError customError={error} />
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type='submit'
            form='new-team-form'
            loading={submitting}
            disabled={teamLimitReached}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NewTeamModal
